import axios from 'axios';
import type { AMIResponse, LineageItem, AccountsResponse, ProcessedAMI, APIResponse, UniqueFile, HashOccurrence, FileDiff, FileSearchRequest, FileSearchResult, ImageNameSearchResult, DailyStatsSummaryResponse, PaginatedDailyAMIResponse, DBStatusResponse, PaginatedAMIResponse, AMICountResponse } from '../types/api';

const API_BASE_URL = import.meta.env.DEV 
  ? '/api'  // Use proxy in development
  : import.meta.env.VITE_API_BASE_URL || 'https://8dqvliaqy2.execute-api.us-west-2.amazonaws.com';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

export const getAMIById = async (imageId: string): Promise<APIResponse<AMIResponse>> => {
  try {    
    const response = await api.get(`/database/imageId/${imageId}`);
    return { data: response.data, status: response.status };
  } catch (error) {
    if (axios.isAxiosError(error)) {
      return {
        data: {} as AMIResponse,
        error: error.message,
        status: error.response?.status || 500,
      };
    }
    throw error;
  }
};

export const getAMILineage = async (imageId: string): Promise<APIResponse<LineageItem[]>> => {
  try {
    const response = await api.get(`/database/imageId/${imageId}/lineage`);
    return { data: response.data, status: response.status };
  } catch (error) {
    if (axios.isAxiosError(error)) {
      return {
        data: [],
        error: error.message,
        status: error.response?.status || 500,
      };
    }
    throw error;
  }
};

export const searchImageNames = async (query: string, limit?: number): Promise<APIResponse<ImageNameSearchResult[]>> => {
  try {
    const params = new URLSearchParams({ q: query });
    if (limit) {
      params.append('limit', limit.toString());
    }
    
    const response = await api.get(`/database/search/imageName?${params.toString()}`, {
      headers: {
        'Accept-Encoding': 'gzip',
      },
    });
    return { data: response.data, status: response.status };
  } catch (error) {
    if (axios.isAxiosError(error)) {
      return {
        data: [],
        error: error.response?.data || error.message,
        status: error.response?.status || 500,
      };
    }
    throw error;
  }
};

// Legacy function - now uses RDS backend for better search performance
export const searchAMIs = async (query: string): Promise<APIResponse<AMIResponse[]>> => {
  try {
    // Use new RDS-backed search for name queries
    const imageSearchResponse = await searchImageNames(query, 1000);
    
    if (imageSearchResponse.error) {
      return {
        data: [],
        error: imageSearchResponse.error,
        status: imageSearchResponse.status,
      };
    }
    
    // Convert ImageNameSearchResult to AMIResponse format for backward compatibility
    const convertedData: AMIResponse[] = imageSearchResponse.data.map(result => ({
      amiId: result.imageId,
      name: result.imageName,
      ownerId: result.accountId,
      imageOwnerAlias: result.imageOwnerAlias,
      firstSeen: result.createdAt,
      lastSeen: result.processedAt,
      regionFound: result.regionFound,
      description: result.description,
      creationDate: result.createdAt,
      deprecationTime: '',
      imageLocation: '',
      platformDetails: result.platformDetails,
      architecture: result.platformDetails, // Use platformDetails for architecture field
      imdsSupport: '',
      rootDeviceType: '',
      sourceImageId: '',
      sourceImageRegion: '',
    }));
    
    return { data: convertedData, status: imageSearchResponse.status };
  } catch (error) {
    if (axios.isAxiosError(error)) {
      return {
        data: [],
        error: error.message,
        status: error.response?.status || 500,
      };
    }
    throw error;
  }
};

export const getAMIsByAccountId = async (
  accountId: string,
  limit?: number,
  offset?: number
): Promise<APIResponse<PaginatedAMIResponse>> => {
  try {
    const params: Record<string, number> = {};
    if (limit !== undefined) params.limit = limit;
    if (offset !== undefined) params.offset = offset;

    const response = await api.get(`/database/accountId/${accountId}`, { params });
    return { data: response.data, status: response.status };
  } catch (error) {
    if (axios.isAxiosError(error)) {
      return {
        data: { amis: [], pagination: { total: 0, limit: 100, offset: 0, hasMore: false } },
        error: error.message,
        status: error.response?.status || 500,
      };
    }
    throw error;
  }
};

export const getAMICountByAccountId = async (accountId: string): Promise<APIResponse<AMICountResponse>> => {
  try {
    const response = await api.get(`/database/accountId/${accountId}/amiCount`);
    return { data: response.data, status: response.status };
  } catch (error) {
    if (axios.isAxiosError(error)) {
      return {
        data: { accountId, count: 0 },
        error: error.message,
        status: error.response?.status || 500,
      };
    }
    throw error;
  }
};

export const getAccounts = async (): Promise<APIResponse<AccountsResponse>> => {
  try {
    const response = await api.get('/database/accounts');
    return { data: response.data, status: response.status };
  } catch (error) {
    if (axios.isAxiosError(error)) {
      return {
        data: { accounts: [] },
        error: error.message,
        status: error.response?.status || 500,
      };
    }
    throw error;
  }
};

export const getAMIFileHashes = async (imageId: string): Promise<APIResponse<string>> => {
  try {
    const response = await api.get(`/imageId/${imageId}/files/hashes`, { responseType: 'text' });
    return { data: response.data, status: response.status };
  } catch (error) {
    if (axios.isAxiosError(error)) {
      return {
        data: '',
        error: error.message,
        status: error.response?.status || 500,
      };
    }
    throw error;
  }
};

export const getAMIFileTimestamps = async (imageId: string): Promise<APIResponse<string>> => {
  try {
    const response = await api.get(`/imageId/${imageId}/files/timestamps`, { responseType: 'text' });
    return { data: response.data, status: response.status };
  } catch (error) {
    if (axios.isAxiosError(error)) {
      return {
        data: '',
        error: error.message,
        status: error.response?.status || 500,
      };
    }
    throw error;
  }
};

export const getRecentAMIs = async (): Promise<APIResponse<ProcessedAMI[]>> => {
  try {
    // Fetch from cached S3 data via CloudFront for better performance
    // Development uses proxy, production uses CloudFront directly (CORS now configured)
    const CLOUDFRONT_BASE_URL = import.meta.env.DEV
      ? '/cloudfront-proxy'
      : 'https://d3dubqvfuelha7.cloudfront.net/data';
    
    const response = await axios.get(`${CLOUDFRONT_BASE_URL}/processed-amis.json`, {
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
    });
    return { data: response.data, status: response.status };
  } catch (error) {
    if (axios.isAxiosError(error)) {
      return {
        data: [],
        error: error.message,
        status: error.response?.status || 500,
      };
    }
    throw error;
  }
};

export const getAMIUniqueFileHashes = async (imageId: string): Promise<APIResponse<UniqueFile[]>> => {
  try {
    const response = await api.get(`/database/imageId/${imageId}/hashes/unique`);
    return { data: response.data, status: response.status };
  } catch (error) {
    if (axios.isAxiosError(error)) {
      return {
        data: [],
        error: error.response?.data || error.message,
        status: error.response?.status || 500,
      };
    }
    throw error;
  }
};

export const checkAMIExistsInHashDB = async (imageId: string): Promise<boolean> => {
  try {
    // Make a lightweight call to check if AMI exists in hash database
    await api.get(`/database/imageId/${imageId}/exists`);
    return true;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      return false;
    }
    // For other errors (timeout, server error, etc.), we can't determine if it exists
    // So we return false to be safe
    return false;
  }
};

export const getAMIDBStatus = async (imageId: string): Promise<DBStatusResponse> => {
  try {
    // Call the new dbStatus endpoint which returns JSON with scan status
    const response = await api.get<DBStatusResponse>(`/database/imageId/${imageId}/dbStatus`);
    return response.data;
  } catch (error) {
    // If the API call fails for any reason, return a safe default
    console.error('Error fetching AMI DB status:', error);
    return {
      exists: false,
    };
  }
};

export const getAMIEC2Status = async (imageId: string, region?: string): Promise<{status: 'public' | 'private' | 'deleted' | 'unknown', data?: unknown}> => {
  try {
    const params = region ? { region } : {};
    const response = await api.get(`/database/imageId/${imageId}/status`, { params });
    
    if (response.data && !response.data.error) {
      return { status: 'public', data: response.data };
    } else if (response.data && response.data.error === 'Image not found in EC2') {
      return { status: 'private' };
    } else if (response.data && typeof response.data.error === 'string' && response.data.error.includes('InvalidAMIID.NotFound')) {
      return { status: 'deleted' };
    } else if (response.data && typeof response.data.error === 'string' && response.data.error.includes('Image not found in EC2 in any region')) {
      return { status: 'deleted' };
    }
    
    return { status: 'unknown' };
  } catch (error) {
    if (axios.isAxiosError(error) && typeof error.response?.data?.error === 'string') {
      const message = error.response.data.error;
      if (message.includes('InvalidAMIID.NotFound')) {
        return { status: 'deleted' };
      }
      if (message === 'Image not found in EC2' || message.includes('Image not found in EC2')) {
        return { status: 'private' };
      }
    }
    return { status: 'unknown' };
  }
};

export const getAMIsByHash = async (hashId: string): Promise<APIResponse<HashOccurrence[]>> => {
  try {
    const response = await api.get(`/database/hash/${hashId}`);
    return { data: response.data, status: response.status };
  } catch (error) {
    if (axios.isAxiosError(error)) {
      return {
        data: [],
        error: error.message,
        status: error.response?.status || 500,
      };
    }
    throw error;
  }
};

export const compareAmis = async (newImageId: string, oldImageId: string): Promise<APIResponse<FileDiff[]>> => {
  try {
    const response = await api.get(`/database/compare/${newImageId}/${oldImageId}`);

    // Handle null or undefined responses from the API
    if (response.data === null || response.data === undefined) {
      return {
        data: [],
        error: 'One or both AMIs not found in the database',
        status: response.status || 404,
      };
    }

    return { data: response.data, status: response.status };
  } catch (error) {
    if (axios.isAxiosError(error)) {
      return {
        data: [],
        error: error.response?.data?.error || error.message,
        status: error.response?.status || 500,
      };
    }
    throw error;
  }
};

export const searchFiles = async (request: FileSearchRequest): Promise<APIResponse<FileSearchResult[]>> => {
  try {
    const params = new URLSearchParams({
      searchType: request.searchType,
      matchType: request.matchType,
      searchTerm: request.searchTerm,
    });
    
    if (request.limit) {
      params.append('limit', request.limit.toString());
    }
    
    const response = await api.get(`/database/search/files?${params.toString()}`, {
      headers: {
        'Accept-Encoding': 'gzip',
      },
    });
    return { data: response.data, status: response.status };
  } catch (error) {
    if (axios.isAxiosError(error)) {
      return {
        data: [],
        error: error.response?.data || error.message,
        status: error.response?.status || 500,
      };
    }
    throw error;
  }
};

export const getCounts = async (): Promise<APIResponse<{ amiCount: number; hashCount: number; accountCount: number; scannedAmisCount: number }>> => {
  try {
    // Fetch from cached S3 data via CloudFront for better performance
    const CLOUDFRONT_BASE_URL = import.meta.env.DEV
      ? '/cloudfront-proxy'
      : 'https://d3dubqvfuelha7.cloudfront.net/data';

    const response = await axios.get(`${CLOUDFRONT_BASE_URL}/counts.json`, {
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
    });
    return { data: response.data, status: response.status };
  } catch (error) {
    if (axios.isAxiosError(error)) {
      return {
        data: { amiCount: 0, hashCount: 0, accountCount: 0, scannedAmisCount: 0 },
        error: error.message,
        status: error.response?.status || 500,
      };
    }
    throw error;
  }
};

export const getDailyStatsSummary = async (): Promise<APIResponse<DailyStatsSummaryResponse>> => {
  try {
    // Fetch from cached S3 data via CloudFront for better performance
    const CLOUDFRONT_BASE_URL = import.meta.env.DEV
      ? '/cloudfront-proxy'
      : 'https://d3dubqvfuelha7.cloudfront.net/data';

    const response = await axios.get(`${CLOUDFRONT_BASE_URL}/daily-stats-summary.json`, {
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
    });
    return { data: response.data, status: response.status };
  } catch (error) {
    if (axios.isAxiosError(error)) {
      return {
        data: { lastUpdated: '', stats: [] },
        error: error.message,
        status: error.response?.status || 500,
      };
    }
    throw error;
  }
};

export const getDailyStatsForDay = async (
  date: string,
  type: 'added' | 'scanned' | 'failed' = 'added',
  limit?: number,
  offset?: number
): Promise<APIResponse<PaginatedDailyAMIResponse>> => {
  try {
    const params: Record<string, string | number> = { type };
    if (limit !== undefined) params.limit = limit;
    if (offset !== undefined) params.offset = offset;

    const response = await api.get(`/database/daily-stats/day/${date}`, { params });
    return { data: response.data, status: response.status };
  } catch (error) {
    if (axios.isAxiosError(error)) {
      return {
        data: { items: [], pagination: { total: 0, limit: limit || 1000, offset: offset || 0, hasMore: false } },
        error: error.message,
        status: error.response?.status || 500,
      };
    }
    throw error;
  }
}; 